import {
  DESCRIPTION_MIN_LENGTH,
  FAQ_ANSWER_MAX_LENGTH,
  FAQ_QUESTION_MAX_LENGTH,
  LISTING_TITLE_MAX_LENGTH,
  MAX_LISTING_IMAGES,
  MAX_PACKAGES,
  PRICING_MODEL_HOURLY,
  PRICING_MODELS,
  getListingPackages,
} from './listingPackages';
import { richTextToText } from './validators';

export const COMPLETENESS_KEYS = ['title', 'images', 'description', 'pricing'];

const trimmedText = value => (typeof value === 'string' ? value.trim() : '');
const richTextTrimmedLength = html => richTextToText(html).trim().length;
const inRange = (n, max) => n >= 1 && n <= max;

// Old listings can hold package texts and descriptions over today's limits.
// Unchanged, they still count as done: the limits apply where the user edits
// (the item editors' field validators and the description field).
export const isPackageValid = pkg =>
  !!pkg &&
  PRICING_MODELS.includes(pkg.pricingModel) &&
  Number.isInteger(pkg.price) &&
  pkg.price > 0 &&
  trimmedText(pkg.title).length > 0 &&
  richTextTrimmedLength(pkg.description) > 0;

export const isFaqItemValid = item =>
  !!item &&
  inRange(trimmedText(item.question).length, FAQ_QUESTION_MAX_LENGTH) &&
  inRange(richTextTrimmedLength(item.answer), FAQ_ANSWER_MAX_LENGTH);

const isPricingDone = packages =>
  Array.isArray(packages) &&
  inRange(packages.length, MAX_PACKAGES) &&
  packages.every(isPackageValid) &&
  packages.filter(pkg => pkg.pricingModel === PRICING_MODEL_HOURLY).length <= 1;

// Published listings can have a description under today's minimum. The editor
// passes `isLegacyDescription` while it is left as it was, so it still counts
// as done; once the user changes it, the minimum applies.
export const getListingCompleteness = (values, { isLegacyDescription = false } = {}) => {
  const { title, images, description, packages } = values;
  const descriptionLength = richTextTrimmedLength(description);
  const done = {
    title: inRange(trimmedText(title).length, LISTING_TITLE_MAX_LENGTH),
    images: Array.isArray(images) && inRange(images.length, MAX_LISTING_IMAGES),
    description: isLegacyDescription
      ? descriptionLength > 0
      : descriptionLength >= DESCRIPTION_MIN_LENGTH,
    pricing: isPricingDone(packages),
  };
  const items = COMPLETENESS_KEYS.map(key => ({ key, done: done[key] }));
  const doneCount = items.filter(item => item.done).length;
  return {
    items,
    doneCount,
    percent: Math.round((doneCount / COMPLETENESS_KEYS.length) * 100),
    isComplete: doneCount === COMPLETENESS_KEYS.length,
    missing: items.filter(item => !item.done).map(item => item.key),
  };
};

export const listingToCompletenessValues = listing => {
  const attributes = (listing && listing.attributes) || {};
  const publicData = attributes.publicData || {};
  return {
    title: attributes.title,
    images: (listing && listing.images) || [],
    description: publicData.descriptionText ?? attributes.description,
    packages: getListingPackages(listing),
  };
};
